/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { View, Text, StyleSheet, ScrollView, Platform, Dimensions, RefreshControl } from "react-native";
import { useTheme } from "../theme";
import { useSelector, useDispatch } from "react-redux";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import ObservationWelcome from "./observation/ObservationWelcome";
import ObservationMetrics from "./observation/ObservationMetrics";
import ObservationBegin from "./observation/ObservationBegin";
import ObservationDetails from "./observation/ObservationDetails";
import ObservationFilters from "./observation/ObservationFilters";
import MobileFilterOverlay from "../components/MobileFilterOverlay";
import ObservationErrorModal from "./observation/components/ObservationErrorModal";
import { fetchObservationsMetrics } from "./observation/fetchObservationsMetrics";
import { getDropDownfilterOptions } from "./observation/dropDownfilterOptions";
import { fetchObservationDetails } from "./observation/fetchObservationDetails";
import { handleViewFilterChange as handleViewFilterChangeUtil } from "./observation/handleViewFilterChange";
import { fetchObservationsData } from "./observation/fetchObservationsData";
import {
  clearSuccessMessage,
  setSelectedFilters,
  setObservationViewFilters,
} from "../store/slices/observationSlice";

import {
  setSelectedRecord,
  setPulseState,
} from "../store/slices/pulseCheckSlice";

// Import data from data/index.ts
import {
  observationFilterOptions as filterOptions,
  observationTableColumns as tableColumns,
  observationTabs as tabs,
} from "../data";

// Import from types file
import {
  DropdownItem,
  ObservationRowData,
  ObservationAPIData,
} from "../types/observation";
import { formatUserName } from "../utils/emailTouserName";
import { getYearOptions } from "../utils/filterOptions";
import { useRefreshRoles } from "../hooks";
// Add explicit types for props in the Observation component
interface ObservationProps {
  onEmployeeUpdateClick: () => void;
  onPulseCheckScreenClick: () => void; // This navigates to PulseCheck component from components/PulseCheck/index.tsx
}

const Observation: React.FC<ObservationProps> = ({
  onEmployeeUpdateClick,
  onPulseCheckScreenClick,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const dispatch = useDispatch();

  // Refresh roles on every mount so recent admin changes are reflected in dropdowns
  useRefreshRoles();

  const [filters, setFilters] = useState({
    employee: "",
    role: "",
    observationType: "",
    shift: "",
  });
  const [activeTab, setActiveTab] = useState("previous-observations");
  const [selectedRowData, setSelectedRowData] = useState<ObservationRowData | null>(null);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [isFilterOverlayVisible, setFilterOverlayVisible] = useState(false);
  const [isMobileFilterOverlayVisible, setMobileFilterOverlayVisible] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [observationsData, setObservationsData] = useState<ObservationRowData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Add state for detailed observation data and full observations data
  const [detailedObservationData, setDetailedObservationData] = useState<
    any | null
  >(null);
  const [fullObservationsData, setFullObservationsData] = useState<
    ObservationAPIData[]
  >([]);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailApiError, setDetailApiError] = useState<string | null>(null); // We'll use the formatDate function from the utility instead
  const [refreshing, setRefreshing] = useState(false);

  // Get data from Redux state
  const { selectedLocation, employees, roles, periodWeek } = useSelector(
    (state: any) => state.appData
  );
  const { successMessage, selectedObservationViewFilters } = useSelector(
    (state: any) => state.observation
  );

  // Get filter options from the utility function
  const dropDownfilterOptions = getDropDownfilterOptions(employees, roles);

  // Add dynamic filter options to the static options from data/index.ts
  const dynamicFilterOptions = React.useMemo(() => {
    const baseOptions = [...filterOptions];
    const createdByIndex = baseOptions.findIndex(
      (opt) => opt.key === "createdBy"
    );
    const modifiedByIndex = baseOptions.findIndex(
      (opt) => opt.key === "modifiedBy"
    );
    const roleIndex = baseOptions.findIndex((opt) => opt.key === "role");
    const employeeIndex = baseOptions.findIndex(
      (opt) => opt.key === "employee"
    );
    
    // Add year filter
    baseOptions.push({
      key: "year",
      label: "Year",
      options: getYearOptions(),
    });

    // Add employee data from Redux state in the same format as getDropDownfilterOptions
    if (
      employees &&
      employees.data &&
      employees.data.length > 0 &&
      employeeIndex !== -1
    ) {
      const employeeOptions = employees.data
        .filter((emp: any) => emp.Active_vs_Inactive === "Active") // Only include active employees
        .filter((emp: any) => Number(emp.New_Hire) === 0) // Exclude new hires
        .map((emp: any) => ({
          id: String(emp.Id || emp.GPID),
          displayText: `${emp.First_Name} ${emp.Last_Name}`,
          gpid: emp.GPID, // Store GPID for filtering
        }));
      baseOptions[employeeIndex] = {
        ...baseOptions[employeeIndex],
        options: employeeOptions,
      };
    }

    // Add roles data from Redux state in the same format as getDropDownfilterOptions
    if (roles && roles.length > 0 && roleIndex !== -1) {
      const roleOptions = roles.map(
        (role: { ROLE_ID: string | number; ROLE_NAME: string }) => ({
          id: String(role.ROLE_ID),
          displayText: role.ROLE_NAME,
        })
      );
      baseOptions[roleIndex] = {
        ...baseOptions[roleIndex],
        options: roleOptions,
      };
    }

    if (fullObservationsData.length > 0) {
      // Use all unique emails from the fullObservationsData for Created_By and Modified_By
      const allCreatedByEmails = Array.from(
        new Set(
          fullObservationsData.map((item) => item.Created_By).filter(Boolean)
        )
      );
      const allModifiedByEmails = Array.from(
        new Set(
          fullObservationsData.map((item) => item.Modified_By).filter(Boolean)
        )
      );

      const createdByOptions = allCreatedByEmails.map((email) => ({
        id: email, // id is email, displayText is name
        displayText: formatUserName(email),
        email,
      }));
      const modifiedByOptions = allModifiedByEmails.map((email) => ({
        id: email,
        displayText: formatUserName(email),
        email,
      }));

      if (createdByIndex !== -1) {
        baseOptions[createdByIndex] = {
          ...baseOptions[createdByIndex],
          options: createdByOptions,
        };
      }
      if (modifiedByIndex !== -1) {
        baseOptions[modifiedByIndex] = {
          ...baseOptions[modifiedByIndex],
          options: modifiedByOptions,
        };
      }
    }

    // Add selectedOptionId mapping based on Redux selectedObservationViewFilters
    return baseOptions.map((option) => {
      const mappedKey = {
        observationType: "observationType",
        shift: "shiftNumber", 
        role: "roleObserved",
        progress: "status",
        createdBy: "createdBy",
        modifiedBy: "modifiedBy",
        period: "period",
        week: "week",
        year: "year"
      }[option.key] || option.key;

      const selectedValue = selectedObservationViewFilters[mappedKey];
      
      // For createdBy/modifiedBy, match by email (id) since value stored is email
      if (option.key === 'createdBy' || option.key === 'modifiedBy') {
        const matchedOption = option.options?.find((opt: any) => opt.id === selectedValue);
        return {
          ...option,
          selectedOptionId: matchedOption ? matchedOption.id : ""
        };
      }
      
      // For other filters, match by displayText
      const matchedOption = option.options?.find((opt: any) => opt.displayText === selectedValue);
      return {
        ...option,
        selectedOptionId: matchedOption ? matchedOption.id : ""
      };
    });
  }, [filterOptions, fullObservationsData, roles, getYearOptions, selectedObservationViewFilters]);

  // Helper function to check if device is iPhone (iOS mobile only)
  const isIOSMobile = () => {
    const windowWidth = Dimensions.get("window").width;
    return Platform.OS === "ios" && windowWidth < 768; // iPhone only, not iPad
  };
  
  // Function to fetch observations data with filters - wrapper around the utility function
  const handleFetchObservationsWithFilters = async () => {
    await fetchObservationsData({
      selectedLocation,
      setIsLoading,
      setApiError,
      setObservationsData,
      setFullData: setFullObservationsData,
      dispatch,
      selectedObservationViewFilters,
      applyFilters: true,
    });
  };

  // Auto-apply filters on selection change (debounced)
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      handleFetchObservationsWithFilters();
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [selectedObservationViewFilters]);

  // Function to fetch detailed observation data from API
  const handleFetchObservationDetails = async (observationId: number) => {
    // Call the extracted function with the required parameters
    await fetchObservationDetails(
      observationId,
      setIsDetailLoading,
      setDetailApiError,
      setDetailedObservationData,
      dispatch
    );
  };
  // Wrapper function to call the imported utility
  // For Created By/Finalized By, send email in payload, but show name in dropdown
  const handleViewFilterChange = (
    key: string,
    value: DropdownItem | DropdownItem[] | null
  ) => {
    // For createdBy/modifiedBy, always send only the email (id) in the payload
    if ((key === "createdBy" || key === "modifiedBy") && value) {
      const selectedItem = Array.isArray(value) ? value[0] : value;
      if (selectedItem && selectedItem.id && selectedItem.id !== "all") {
        handleViewFilterChangeUtil(
          key,
          { id: selectedItem.id, displayText: selectedItem.id },
          dispatch
        );
        return;
      }
    }
    // Default behavior for other filters
    handleViewFilterChangeUtil(key, value, dispatch);
  };

  const handleRowClick = (
    rowData: ObservationRowData & { 
      isPulseCheckNavigation?: boolean;
      columnKey?: string;
    }
  ) => {
    console.log("Row clicked data:", rowData);
    console.log("columnKey:", rowData.columnKey);
    console.log(
      "isPulseCheckNavigation value:",
      rowData.isPulseCheckNavigation
    );

    // Check if this is coming from a "Type" cell click OR mobile first column click
    if (rowData.isPulseCheckNavigation || rowData.columnKey === "type" || rowData.columnKey === "details") {
      console.log(
        "Type/first column clicked, navigating to Pulse Check component (src/components/PulseCheck/index)"
      );

      // 1. Update the selected filters with data from the row
      // Find the employee information in Redux state
      const employeeInfo = employees?.data?.find(
        (emp: any) => emp.GPID === rowData.employee
      );

      // Get first name and last name from employee info
      const firstName = employeeInfo?.First_Name || "";
      const lastName = employeeInfo?.Last_Name || "";
      const fullName =
        firstName && lastName ? `${firstName} ${lastName}` : rowData.employee;

      // Update selected filters in the observation slice
      dispatch(
        setSelectedFilters({
          employee: fullName, // Set the full name
          employeeFirstName: firstName,
          employeeLastName: lastName,
          employeeGpid: rowData.employee,
          role: rowData.rawData?.Role_Observed || "",
          observationType: rowData.type,
          shift: rowData.rawData?.Shift_Number || "",
        })
      );

      // 2. Set the selected record in the pulse check slice
      dispatch(setSelectedRecord(rowData));

      // 3. Set the pulse state based on the data status
      // Determine pulse state based on rowData.action (Status)
      const pulseState =
        rowData.action?.toLowerCase() === "completed"
          ? "completed"
          : rowData.action?.toLowerCase() === "in progress"
          ? "in-progress"
          : "new";

      dispatch(setPulseState(pulseState));

      // Navigate to the PulseCheck screen
      onPulseCheckScreenClick(); // This navigates to components/PulseCheck/index.tsx
      return;
    }

    // Normal row click behavior for other columns
    setSelectedRowData(rowData);
    console.log("Row clicked:", rowData); // Fetch detailed observation data when a row is clicked
    if (rowData.id) {
      handleFetchObservationDetails(rowData.id);
    }
    setOverlayVisible(true);
  };

  const closeOverlay = () => {
    setOverlayVisible(false);
    setSelectedRowData(null);
  };

  const openFilterOverlay = () => {
    setFilterOverlayVisible(true);
  };

  const closeFilterOverlay = () => {
    setFilterOverlayVisible(false);
  };

  const handleSaveFilters = () => {
    // Check if any required fields are empty
    const requiredFieldsEmpty = Object.values(filters).some(
      (value) => value === ""
    );

    if (requiredFieldsEmpty) {
      // Show validation errors and don't proceed
      setShowValidation(true);
    } else {
      // All required fields are filled, proceed
      setShowValidation(false);
      closeFilterOverlay();
      // Reset stale record and pulse state before starting a new observation
      dispatch(setSelectedRecord(null));
      dispatch(setPulseState("new"));
      onPulseCheckScreenClick();
    }
  };

  // Mobile filter handlers
  const handleMobileFilterPress = () => {
    setMobileFilterOverlayVisible(true);
  };

  const handleMobileFilterCancel = () => {
    setMobileFilterOverlayVisible(false);
  };

  const handleMobileFilterProceed = async (
    nextFilters: Partial<Record<string, any>> = {}
  ) => {
    // Apply the filters to the current view filters
    Object.entries(nextFilters).forEach(([key, value]) => {
      handleViewFilterChange(key, value);
    });

    // Apply the filters by fetching data
    await handleFetchObservationsWithFilters();
    
    // Close the mobile filter overlay
    setMobileFilterOverlayVisible(false);
  };

  // Function to render filter dropdowns using the ObservationFilters component
  const renderFilterDropdowns = () => {
    return (
      <ObservationFilters
        dropDownfilterOptions={dropDownfilterOptions}
        filters={filters}
        setFilters={setFilters}
        showValidation={showValidation}
      />
    );
  };

  // Helper function to call the imported fetchObservationsMetrics
  const fetchObseravationsMetrics = useCallback(async () => {
    // Call the extracted function with the required parameters
    await fetchObservationsMetrics({
      selectedLocation,
      periodWeek,
      employees, // Add employees data from Redux state
      setIsLoading,
      setApiError,
      dispatch,
    });
  }, [
    selectedLocation,
    periodWeek,
    employees,
    setIsLoading,
    setApiError,
    dispatch,
  ]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    const timeout = new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('refresh_timeout')), 3000)
    );
    try {
      await Promise.race([
        (async () => {
          await fetchObseravationsMetrics();
          await fetchObservationsData({
            selectedLocation,
            setIsLoading,
            setApiError,
            setObservationsData,
            setFullData: setFullObservationsData,
            dispatch,
            selectedObservationViewFilters,
            applyFilters: false,
          });
        })(),
        timeout,
      ]);
    } catch {
      // timeout or error — silently cancel
    } finally {
      setRefreshing(false);
    }
  }, [fetchObseravationsMetrics, selectedLocation, selectedObservationViewFilters, dispatch]);

  // Function to reset filters and reload data without filters
  const resetFilters = useCallback(async () => {
    try {
      setIsLoading(true);
      // Reset all filter state to default values
      const defaultFilters = {
        employee: "",
        role: "",
        observationType: "",
        shift: "",
      };
      setFilters(defaultFilters);
      
      // Clear Redux filters
      dispatch(
        setObservationViewFilters({})
      );
      
      // Fetch observations data without any filters
      await fetchObservationsData({
        selectedLocation,
        setIsLoading,
        setApiError,
        setObservationsData,
        setFullData: setFullObservationsData,
        dispatch,
        selectedObservationViewFilters: {},
        applyFilters: false,
      });
    } catch (err) {
      console.error("Error resetting filters:", err);
      setApiError(
        err instanceof Error ? err.message : "Failed to reset filters"
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedLocation,
    dispatch,
    setIsLoading,
    setApiError,
    setObservationsData,
    setFullObservationsData,
  ]);

  // Effect for fetching data when location changes
  useEffect(() => {
    // Fetch observations metrics when component mounts or location changes
    fetchObseravationsMetrics();

    // Always fetch data when selectedLocation changes or component mounts
    // which depends on selectedObservationViewFilters
    fetchObservationsData({
      selectedLocation,
      setIsLoading,
      setApiError,
      setObservationsData,
      setFullData: setFullObservationsData,
      dispatch,
      selectedObservationViewFilters,
      applyFilters: false,
    });
  }, [selectedLocation, dispatch, fetchObseravationsMetrics]);

  // Separate effect for handling success message
  useEffect(() => {
    // Handle success message with timer if present
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 5000);

      // Cleanup timer on unmount or when dependencies change
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: isDark ? colors.background.page : '#F5F9FF' }]}
      contentContainerStyle={{
        flexGrow: 1,
        backgroundColor: isDark ? colors.background.page : '#F5F9FF',
      }}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          tintColor="#0066CC"
          colors={["#0066CC"]}
          title="Refreshing..."
        />
      }
    >
      {successMessage && (
        <View style={styles.successMessageContainer}>
          <MaterialIcons
            name="check-circle"
            size={24}
            color="#008242"
            style={styles.successIcon}
          />
          <Text style={styles.successMessageText}>{successMessage}</Text>
        </View>
      )}
      <ObservationWelcome
        onEmployeeUpdateClick={onEmployeeUpdateClick}
        openFilterOverlay={openFilterOverlay}
      />
      {/* Observation Metrics Component */}
      <ObservationMetrics
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        filterOptions={dynamicFilterOptions}
        handleFilterChange={handleViewFilterChange}
        isLoading={isLoading}
        apiError={apiError}
        observationsData={observationsData}
        tableColumns={tableColumns}
        handleRowClick={handleRowClick}
        resetFilters={resetFilters}
        showMobileFilterButton={isIOSMobile()}
        onMobileFilterPress={handleMobileFilterPress}
      />

      {/* Observation Details Overlay Component */}
      <ObservationDetails
        selectedRowData={selectedRowData}
        isOverlayVisible={isOverlayVisible}
        closeOverlay={closeOverlay}
        isDetailLoading={isDetailLoading}
        detailApiError={detailApiError}
        detailedObservationData={detailedObservationData}
        observationNotes={selectedRowData?.observationNotes}
      />
      {/* Filter Overlay for Begin button */}
      <ObservationBegin
        isFilterOverlayVisible={isFilterOverlayVisible}
        closeFilterOverlay={closeFilterOverlay}
        renderFilterDropdowns={renderFilterDropdowns}
        handleSaveFilters={handleSaveFilters}
      />

      {/* Mobile Filter Overlay - iOS mobile only */}
      {/* <MobileFilterOverlay
        visible={isMobileFilterOverlayVisible}
        onDismiss={handleMobileFilterCancel}
        filters={dynamicFilterOptions}
        onFilterChange={handleViewFilterChange}
        onProceed={handleMobileFilterProceed}
        onCancel={handleMobileFilterCancel}
        currentFilters={selectedObservationViewFilters}
      /> */}

      {/* Centralized Error Modal */}
      <ObservationErrorModal />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: Platform.OS === "web" ? "1.5%" : 16,
    backgroundColor: '#F5F9FF',
    paddingVertical: "1.5%",
  },
  responsiveDropdownsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    flex: 1,
    marginRight: 16,
  },
  responsiveDropdown: {
    width: Platform.OS === "web" ? "48%" : "100%",
    marginBottom: 12,
  },
  successMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
  },
  successIcon: {
    marginRight: 8,
  },
  successMessageText: {
    color: "#155724",
    fontSize: 16,
    flex: 1,
  },
});

export default Observation;
